/**
 * @file metricsRegistry.js
 * @description Central registry for environmental, crop, and hardware
 * telemetry metrics.
 *
 * Maps backend telemetry keys to localization keys, display units, and
 * category groupings (e.g., 'weather', 'realtime', 'crop'). Provides
 * fallback formatting for dynamic/custom sensors.
 *
 * @module constants/metricsRegistry
 * @exports METRIC_REGISTRY
 * @exports getMetricMetadata
 * @exports groupMetrics
 */
export const METRIC_REGISTRY = {
  temperature: {
    id: 'temperature',
    labelKey: 'metrics.temperature.label',
    defaultLabel: 'Temperature',
    unit: '°C',
    category: 'weather'
  },
  humidity: {
    id: 'humidity',
    labelKey: 'metrics.humidity.label',
    defaultLabel: 'Humidity',
    unit: '%',
    category: 'weather'
  },
  dewpoint: {
    id: 'dewpoint',
    labelKey: 'metrics.dewpoint.label',
    defaultLabel: 'Dew Point',
    unit: '°C',
    category: 'weather'
  },
  precipitation: {
    id: 'precipitation',
    labelKey: 'metrics.precipitation.label',
    defaultLabel: 'Precipitation',
    unit: 'mm',
    category: 'weather'
  },
  pressure: {
    id: 'pressure',
    labelKey: 'metrics.pressure.label',
    defaultLabel: 'Pressure',
    unit: 'hPa',
    category: 'weather'
  },
  solar_rad: {
    id: 'solar_rad',
    labelKey: 'metrics.solar_radiation.label',
    defaultLabel: 'Solar Radiation',
    unit: 'W/m²',
    category: 'weather'
  },
  vpd: {
    id: 'vpd',
    labelKey: 'metrics.vpd_kpa.label',
    defaultLabel: 'Vapor Pressure Deficit',
    unit: 'kPa',
    category: 'realtime'
  },
  leaf_wetness_duration_hrs: {
    id: 'leaf_wetness_duration_hrs',
    labelKey: 'metrics.leaf_wetness_duration_hrs.label',
    defaultLabel: 'Leaf Wetness',
    unit: 'hrs',
    category: 'realtime'
  }
};

export function getMetricMetadata(key) {
  if (METRIC_REGISTRY[key]) return METRIC_REGISTRY[key];

  // Dynamic fallback formatting for unmapped keys
  const lower = key.toLowerCase();
  const cleaned = key.replace(/_(percent|c|kpa|mm|hpa|wm2|hrs)$/i, '');
  let label = cleaned.replace(/[:_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // Centralized heuristic guessing for unknown dynamic sensors
  let labelKey = '';
  let unit = '';
  if (lower.includes('temp') || lower.includes('dew')) unit = '°C';
  else if (lower.includes('humidity') || lower.includes('risk')) unit = '%';

  let category = 'realtime';
  if (lower.includes('weather') || lower.includes('temp') || lower.includes('wind') || lower.includes('humidity') || lower.includes('pressure')) {
    category = 'weather';

  } else if (lower.includes('realtime') || lower.includes('vpd') || lower.includes('dew') || lower.includes('wetness')) {
    category = 'realtime';

  } else if (lower.includes('degree_days')) {
    category = 'crop';
    labelKey = 'metrics.degree_days_total.label';
    //label = 'Degree Days';
    unit = 'GDD';

  } else if (lower.includes('degree_hours')) {
    category = 'crop';
    labelKey = 'metrics.degree_days_total.label';
    //label = 'Degree Hours';
    unit = 'GDH';

  } else if (lower.includes('crop') || lower.includes('pest') || lower.includes('degree') || lower.includes('mildew') || lower.includes('stage')) {
    category = 'crop';
  }

  return { id: key, labelKey, defaultLabel: label, unit, category };
}

/**
 * Transforms an array of metric keys into standardized UI grouping arrays.
 * Can be reused anywhere in the app to generate display controls.
 */
export function groupMetrics(metricKeys = []) {
  const groups = {};

  // If no keys provided, group everything in the registry
  const keysToGroup = Array.isArray(metricKeys) && metricKeys.length > 0
    ? metricKeys
    : Object.keys(METRIC_REGISTRY);

  keysToGroup.forEach(key => {
    const meta = getMetricMetadata(key);
    const category = meta.category || 'custom';

    if (!groups[category]) {
      groups[category] = [];
    }

    // Prevent duplicates
    if (!groups[category].find(m => m.type === key)) {
      groups[category].push({
        type: key, // Kept for backwards compatibility with your UI controls
        id: meta.id,
        label: meta.defaultLabel,
        labelKey: meta.labelKey,
        unit: meta.unit
      });
    }
  });

  return groups;
}
